<template>
  <section class="contact-section bg-gradient-to-br from-blue-400 to-blue-500 py-20">
    <div class="container mx-auto px-4 max-w-4xl">
      <h2 class="text-3xl md:text-4xl font-bold text-center text-white my-12">
        {{ t('contact.title') }}
      </h2>
      
      <div class="bg-white/10 backdrop-blur-sm rounded-xl shadow-lg p-8 border border-white/20">
        <form @submit.prevent="submitForm" class="space-y-6">
          <!-- Two Column Layout for Desktop -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Full Name -->
            <div class="form-group">
              <label for="full_name" class="block text-white/80 mb-2">
                {{ t('contact.full_name') }}
              </label>
              <input
                type="text"
                id="full_name"
                v-model="full_name"
                :placeholder="t('contact.placeholder.full_name')"
                required
                class="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
              >
            </div>

            <!-- Email -->
            <div class="form-group">
              <label for="email" class="block text-white/80 mb-2">
                {{ t('contact.email') }}
              </label>
              <input
                type="email"
                id="email"
                v-model="email"
                :placeholder="t('contact.placeholder.email')"
                required
                class="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                @focus="checkToken"
              >
            </div>

            <!-- Phone Number -->
            <div class="form-group">
              <label for="phone" class="block text-white/80 mb-2">
                {{ t('contact.phone') }}
              </label>
              <input
                type="tel"
                id="phone"
                v-model="phone"
                :placeholder="t('contact.placeholder.phone')"
                required
                class="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                @focus="checkToken"
              >
            </div>

            <!-- Service Type -->
            <div class="form-group">
              <label for="service_needed" class="block text-white/80 mb-2">
                {{ t('contact.service_needed') }}
              </label>
              <select
                id="service_needed"
                v-model="service_needed"
                required
                class="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent appearance-none"
                @focus="checkToken"
              >
                <option value="" disabled selected class="text-gray-800">
                  {{ t('contact.select') }}
                </option>
                <option
                  v-for="(label, key) in currentServices"
                  :key="key"
                  :value="key"
                  class="text-gray-800"
                >
                  {{ label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Message (Full Width) -->
          <div class="form-group">
            <label for="message" class="block text-white/80 mb-2">
              {{ t('contact.message') }}
            </label>
            <textarea
              id="message"
              v-model="message"
              rows="5"
              :placeholder="t('contact.placeholder.message')"
              class="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
              @focus="checkToken"
            ></textarea>
          </div>

          <!-- Submit Button -->
          <div class="pt-4">
            <button
              type="submit"
              class="w-full py-3 px-6 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-lg shadow-md transition-all duration-300 transform hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-700"
            >
              {{ t('contact.submit') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Auth Required Modal -->
    <div v-if="showModal" class="fixed inset-0 flex items-center justify-center bg-black/70 z-50">
      <div class="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4 border border-white/30 transform transition-all duration-300">
        <div class="text-center mb-6">
          <h3 class="text-2xl font-bold text-gray-900 mb-2">
            {{ t('contact.authRequired') }}
          </h3>
          <p class="text-gray-600">
            {{ t('contact.authMessage') }}
          </p>
        </div>
        
        <div class="flex flex-col space-y-3">
          <router-link
            to="/Auth"
            class="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium py-3 px-6 rounded-lg text-center transition-all duration-300"
          >
            {{ t('contact.loginRegister') }}
          </router-link>
          
          <button
            @click="closeModal"
            class="text-gray-600 hover:text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            {{ t('productsSection.close') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div v-if="showSuccessModal" class="fixed inset-0 flex items-center justify-center bg-black/70 z-50">
      <div class="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4 border border-white/30 transform transition-all duration-300">
        <div class="text-center mb-6">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i class="fas fa-check text-green-600 text-2xl"></i>
          </div>
          <h3 class="text-2xl font-bold text-gray-900 mb-2">
            {{ t('contact.successTitle') }}
          </h3>
          <p class="text-gray-600">
            {{ successModalMessage || t('contact.successMessage') }}
          </p>
        </div>
        
        <button
          @click="closeSuccessModal"
          class="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200"
        >
          {{ t('productsSection.close') }}
        </button>
      </div>
    </div>

    <!-- Error Modal -->
    <div v-if="showErrorModal" class="fixed inset-0 flex items-center justify-center bg-black/70 z-50">
      <div class="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4 border border-white/30 transform transition-all duration-300">
        <div class="text-center mb-6">
          <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i class="fas fa-exclamation text-red-600 text-2xl"></i>
          </div>
          <h3 class="text-2xl font-bold text-gray-900 mb-2">
            {{ t('contact.errorTitle') }}
          </h3>
          <p class="text-gray-600">
            {{ errorModalMessage || t('contact.errorMessage') }}
          </p>
        </div>
        
        <button
          @click="closeErrorModal"
          class="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200"
        >
          {{ t('productsSection.close') }}
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { API_BASE_URL } from '@/apiConfig';
import { useHead } from '@vueuse/head';

useHead({
  title: 'Contact | Mega Hissotto – Climatisation & Froid Industriel Algérie',
  meta: [
    { name: 'description', content: 'Contactez Mega Hissotto pour toute demande de devis, installation ou maintenance de climatiseurs, compresseurs INGOTECH, Panasonic, pompes à chaleur et systèmes de froid industriel en Algérie.' },
    { name: 'keywords', content: 'contact Mega Hissotto, devis climatisation Algérie, service client froid industriel, maintenance climatiseur' },
    { property: 'og:title', content: 'Contact | Mega Hissotto' },
    { property: 'og:url', content: 'https://www.mega-hissotto.com/ContactP' },
  ],
  link: [{ rel: 'canonical', href: 'https://www.mega-hissotto.com/ContactP' }]
});

const { t } = useI18n();

// Form data
const full_name = ref('');
const email = ref('');
const phone = ref('');
const service_needed = ref('');
const message = ref('');
const isLoading = ref(false);

// Modal states
const showModal = ref(false);
const showSuccessModal = ref(false);
const showErrorModal = ref(false);
const successModalMessage = ref('');
const errorModalMessage = ref('');

// Services by language
const services = {
  en: {
    installation: 'Installation',
    repair: 'Repair',
    maintenance: 'Maintenance',
    consultation: 'Consultation',
    other: 'Other'
  },
  fr: {
    installation: 'Installation',
    repair: 'Réparation',
    maintenance: 'Maintenance',
    consultation: 'Consultation',
    other: 'Autre'
  }
};

const lang = localStorage.getItem('lang') || 'en';
const currentServices = computed(() => services[lang] || services.en);

// Check if user is authenticated
const checkToken = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    showModal.value = true;
  }
};

// Submit form
const submitForm = async () => {
  const token = localStorage.getItem('token');
  if (!token) {
    showModal.value = true;
    return;
  }

  isLoading.value = true;
  console.log("Submitting form with data:", {
    full_name: full_name.value,
    email: email.value,
    phone: phone.value,
    service_needed: service_needed.value,
    message: message.value,
  });

  try {
    const response = await axios.post(`${API_BASE_URL}/contact`, {
      full_name: full_name.value,
      email: email.value,
      phone: phone.value,
      service_needed: service_needed.value,
      message: message.value,
    }, {
      headers: {
        Authorization: `Bearer ${token}`, // Add the token here
      }
    });
    // 
    console.log("Response from server:", response);

    if (response.status === 200) {
      showSuccessModal.value = true;
      successModalMessage.value = t('contact.successMessage');

      // RESET FORM HERE
      full_name.value = '';
      email.value = '';
      phone.value = '';
      service_needed.value = '';
      message.value = '';
    } else {
      showErrorModal.value = true;
      errorModalMessage.value = response.data.message || t('contact.errorMessage');
    }
  } catch (error) {
    console.error("Error occurred during form submission:", error);

    if (error.response) {
      console.log("Full error response:", error.response);
      console.log("Error data:", error.response.data);
      console.log("Error status:", error.response.status);
      console.log("Error headers:", error.response.headers);

      // Handle specific error messages that might be nested
      if (error.response.data && error.response.data.errors) {
        console.log("Validation errors:", error.response.data.errors);
      }

      // Use the error message from the server, fallback to a generic message
      errorModalMessage.value = error.response.data.message || t('contact.errorMessage');
    } else {
      // In case of network issues or no response from server
      errorModalMessage.value = t('contact.errorMessage');
    }

    showErrorModal.value = true;
  } finally {
    isLoading.value = false;
  }
};




// Close modals
const closeModal = () => {
  showModal.value = false;
};
const closeSuccessModal = () => {
  showSuccessModal.value = false;
};
const closeErrorModal = () => {
  showErrorModal.value = false;
};
</script>

<style scoped>
.contact-section {
  min-height: 100vh;
}

.form-group select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* Animation for modals */
.fixed {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .contact-section {
    padding: 3rem 1rem;
  }
  
  .container {
    padding: 0 1rem;
  }
}
</style>